<template>
  <v-layout v-if="activities.extendedActivity" column class="app-scroll-section">
    <v-flex>
      <v-container>
        <v-layout row wrap class="ma-0">
          <v-flex xs12 class="display-1 pb-4">
            {{ $t('activity_reservation.title', { entityType: entityType }) }}
          </v-flex>
          <v-flex
            v-if="guest"
            xs12
            class="title grey--text text--darken-2 font-weight-light mt-4"
          >
            <v-text-field
              :label="$t('fields.in_whose_name')"
              outlined
              readonly
              hide-details
              :value="getGuestFullName(guest)"
            />
          </v-flex>
          <v-flex
            xs12
            class="title grey--text text--darken-2 font-weight-light mt-4"
          >
            <v-text-field
              :label="$t('fields.requested_date')"
              outlined
              readonly
              hide-details
              :value="displayFormatDateAndHour(requestedMoment)"
            />
          </v-flex>
          <!-- nominal attendees -->
          <v-flex xs12 v-if="nominalAttendees.length" class="grey--text text--darken-2 font-weight-light mt-4">
            <v-autocomplete
              :label="$t('fields.attendees')"
              :items="guestsInBookings"
              :value="nominalAttendees"
              item-value="guest.id"
              append-icon=""
              hide-details
              outlined
              readonly
              multiple
            >
              <template v-slot:selection="{ item }">
                <v-chip>
                  {{ getGuestFullName(item) }}
                </v-chip>
              </template>
            </v-autocomplete>
          </v-flex>
          <!-- numeral attendees -->
          <v-flex xs12 v-if="numeralAttendees.length">
            <v-flex
              v-for="(attendeeType, i) in attendeeTypes"
              :set="value = getNumeralAttendeeValue(attendeeType)"
              :key="i"
              xs12>
              <v-text-field
                v-if="value"
                :label="$t('activity_reservation.attendee_type', { type: attendeeType.name })"
                :value="value"
                class="grey--text text--darken-2 font-weight-light mt-4"
                hide-details
                readonly
                outlined />
            </v-flex>
          </v-flex>
          <v-flex
            v-if="room"
            xs12
            class="title grey--text text--darken-2 font-weight-light mt-4"
          >
            <v-text-field
              :label="$t('fields.room')"
              outlined
              readonly
              hide-details
              :value="room.room.name"
            />
          </v-flex>
          <v-flex
            v-if="orderLines.length || isRoomService"
            xs12
            class="title text--darken-2 font-weight-light pa-0"
          >
            <v-flex xs12 class="display-1 pt-4">
              {{ reservationMethodType !== 'ROOM_SERVICE' ? $t("activity_reservation.reservation_price") : $t("activity_reservation.details") }}
            </v-flex>
            <v-divider class="my-4" />  
            <div class="mt-4">
              <v-row v-if="orderLines.length">
                <order-summary
                  :orderLines="orderLines"
                  :totalByCurrencies="totalByCurrencies"
                  :isPaid="false"
                />
              </v-row>
              <span v-else-if="isRoomService" xs12 class="" style="width: 100%">
                {{ $t("catalog.empty_cart") }}
              </span>
            </div>
          </v-flex>

          <v-flex class="mt-8" xs12>
            <v-textarea
              :label="$t('fields.comments')"
              :placeholder="$t(`reservations.comments.${reservationMethod.type}`)"
                filled
              v-model="comments"
              :counter="maxLengthTextArea"
              :rules="[ data => data === undefined || data.length <= maxLengthTextArea || $t('validations.messages.max', { length: maxLengthTextArea  })]"
            />
          </v-flex>

          <activity-payment-methods v-if="displayActivityPaymentMethods" class="mt-5"/>
        </v-layout>
      </v-container>
    </v-flex>

    <div class="py-5" style="background-color: #F7F9FC;">
      <div class="px-4">
        <h3 class="total-title" v-text="$t('activity_reservation.amount_to_be_paid')"/>

        <h1 class="total-amount mt-3 mb-4" v-text="getPriceString({ amountAndCurrency: totalPrice })"/>
      </div>

      <v-layout
        class="px-4"
        column
      >
        <v-alert
          :value="unselectedPaymentMethod"
          class="normal"
          dense
          text
          type="warning"
          icon="mdi-information-outline"
        >
          {{ $t('activity_reservation.warning.select_payment_method') }}
        </v-alert>

        <v-alert
          :value="qtyProductsRemaining > 0"
          class="normal"
          dense
          text
          type="warning"
          icon="mdi-information-outline"
        >
          {{ $tc('activity_reservation.warning.minimum_order_units', qtyProductsRemaining, { qtyProducts: qtyProductsRemaining }) }}
        </v-alert>

        <v-alert
          :value="orderIsUnpayable || isActiveAccountRequired || getCapacityStatus({ over: true })"
          class="normal"
          dense
          text
          type="error"
        >
          {{ orderIsUnpayable ? $t('error.not_enough_balance') : isActiveAccountRequired ? $t('reservations.inactive_account') : $t("activity_reservation.over_capacity") }}
        </v-alert>
      </v-layout>

      <v-layout justify-center class="ma-2 flex-none p" row>
        <v-flex v-if="!isComponent" xs12 sm6 class="pa-2">
          <v-btn block outlined color="primary" class="primary-gradient" @click="$router.go(-1)">
            {{$t(`activity_reservation.actions.back.${reservationMethod.type}`)}}
          </v-btn>
        </v-flex>
        <v-flex xs12 :sm6="!isComponent" class="pa-2">
          <v-btn
            :disabled="orderIsUnpayable || 
              isActiveAccountRequired || 
              getCapacityStatus({ over: true }) || 
              isProductNeeded || 
              qtyProductsRemaining > 0 || 
              commentTooLong || 
              unselectedPaymentMethod" 
            :loading="loading"
            @click="completeReservation()"
            block
            outlined
            color="primary"
            class="primary-gradient"
            >{{ orderHasPrice ? $t('activity_reservation.actions.confirm_and_pay') : $t('activity_reservation.actions.confirm') }}</v-btn
          >
        </v-flex>
      </v-layout>
    </div>
  </v-layout>
  <v-layout v-else justify-center align-center>
    <v-progress-circular indeterminate />
  </v-layout>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { getGuestFullName } from '@/lib/helpers/GuestName'
import { reservationTypes, orderLineTypes } from '@/config'
import routeNames from '@/router/routes'
import reservationMixins from '@/mixins/Reservation'
import activityMixins from '@/mixins/Activity'
import { displayFormatDateAndHour } from '@/lib/helpers/DateUtils'

export default {
  props: {
    isComponent: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      getGuestFullName,
      loading: false,
      maxLengthTextArea: 150,
      displayFormatDateAndHour,
    }
  },
  computed: {
    ...mapState({
      activities: state => state.activities,
      activityReservations: state => state.activityReservations,
      acceptRoomCharge: state => state.service.configuration.acceptRoomCharge,
      enableRoomCharge: state => state.activities.extendedActivity.activity.enabledWebappPaymentMethods.roomCharge,
      page: state => state.pages.page,
    }),
    ...mapGetters({
      getOrder: 'cart/getOrder',
      getPriceString: 'me/getPriceString',
    }),
    
    entityType () {
      return this.isRoomService ? this.$t('activity_reservation.order') : this.$t('activity_reservation.reservation').toLowerCase()
    },
    commentTooLong () {
      return this.comments ? this.comments.length > this.maxLengthTextArea : false
    },
    qtyProductsRemaining () {
      return this.amountInCart > this.reservationMethod.minimumOrderUnits ? 0 : this.reservationMethod.minimumOrderUnits - this.amountInCart
    },
    isRoomService () {
      return this.reservationMethod.type === reservationTypes.ROOM_SERVICE
    },

    serviceChargesOrderLines () {
      const serviceCharges = this.orderLines.filter((orderLine) => orderLine.orderLineType === orderLineTypes.SERVICE_CHARGE && orderLine.price[0].amount)
      return serviceCharges
    },
    serviceChargesToShow () {
      const setOfValues = new Set()
      const valuesOfServiceCharges = {}
      for (let serviceCharge of this.serviceChargesOrderLines) {
        const { value, quantity, price } = serviceCharge
        const { amount, currency } = price[0]
        if (setOfValues.has(value)) {
          valuesOfServiceCharges[value].amount += amount * quantity
        }
        else {
          setOfValues.add(value)
          valuesOfServiceCharges[value] = {
            amount: amount * quantity,
            currency,
            value,
          }
        }
      }
      return Object.values(valuesOfServiceCharges)
    },
    totalPrice () {
      const { amount: subtotal, currency } = this.totalByCurrencies[0]
      const total = this.serviceChargesToShow.reduce(
        (acc, serviceCharge) => acc + serviceCharge.amount,
        subtotal
      )

      return { amount: total, currency }
    },

    displayActivityPaymentMethods () {
      const totalGreaterThenZero = this.totalPrice.amount > 0
      const totalEqualZero = this.totalPrice.amount === 0
      const roomchargeNotAvailable = !this.acceptRoomCharge || !this.enableRoomCharge

      if (totalEqualZero && !roomchargeNotAvailable) {
        this.$store.commit('activityReservations/setField', {
          field: 'paymentServiceId',
          value: 'roomCharge',
        })
      }

      return totalGreaterThenZero || (totalEqualZero && (roomchargeNotAvailable))
    },

    unselectedPaymentMethod () {
      return this.activityReservations.edited && !this.activityReservations.edited.paymentServiceId
    },
  },
  methods: {
    completeReservation () {
      this.loading = true
      this.getSlotCapacity({ activityId: this.page.pageSpecific.activityId })
        .then(() => {
          if (this.getCapacityStatus({ over: true })) { 
            this.loading = false 
            return 
          }
          const order = this.orderLines.length > 0
            ? this.getOrder(this.reservationOrderLines.filter(({ orderLineType }) => orderLineType !== orderLineTypes.SERVICE_CHARGE))
            : null
          this.$store
            .dispatch('activityReservations/makeReservation', { order })
            .then((data) => {
              if (data.redirectUrl) {
                const params = {
                  activityReservationId: data.activityReservationId,
                  redirectUrl: data.redirectUrl,
                }

                this.$router.push({ name: routeNames.reservationPayment.name, params })
              } else {
                this.$router.push({ name: routeNames.reservationComplete.name })
              }
            })
            .catch((error) => {
              this.$store.commit('state/setError', error)
              this.$router.push({ name: routeNames.reservationError.name, params: { reservationMethod: this.reservationMethod } })
            })
        })
    },
    getNumeralAttendeeValue (attendeeType) {
      const numeralAttendee = this.numeralAttendees.find((numeralAttendee) => numeralAttendee.id === attendeeType.id)
      return numeralAttendee ? numeralAttendee.quantity : null
    },
  },
  mixins: [
    reservationMixins,
    activityMixins,
  ],
  components: {
    OrderSummary: () => import('@/components/view-components/catalog/OrderSummary.vue'),
    ActivityPaymentMethods: () => import('@/components/view-components/activities/ActivityPaymentMethods.vue'),
  },
}
</script>

<style lang="scss">
.total-title {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 21px;
  letter-spacing: 0.25px;

  color: #1A1D44;
}

.total-amount {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 30px;
  line-height: 35px;
  display: flex;
  align-items: center;
  letter-spacing: 0.25px;

  color: var(--v-primary-base);
}

.v-alert--text.normal:before {
  opacity: .12 !important;
}
</style>
